import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { PktrAppMenuService } from '@prospektr/pktr-app-menu';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Campaign } from 'src/app/_shared/interface/campaign.interface';
import { CampaignService } from 'src/app/_shared/service/campaign.service';
import { CampaignState } from 'src/app/_shared/state/campaign.state';
import { ContactState } from 'src/app/_shared/state/contact.state';
import { AppState } from 'src/app/app.state';
import { ValidatorsService } from 'src/app/shared/service/validators.service';
import constants from 'src/constant.json';

@Component({
  selector: 'app-campaign-info',
  templateUrl: './campaign-info.component.html',
  styleUrls: ['./campaign-info.component.scss'],
})
export class CampaignInfoComponent implements OnInit {
  @Input() selectedInfo: any;
  @Output() isOpenInfoEvent = new EventEmitter<any>();
  @ViewChild(MatPaginator) paginator: MatPaginator;

  statusId: any;
  selectedEmailStatus: string = 'all';
  infoFetchParams = { limit: 10, offset: 0, searchQuery: null, emailStatus: 'read' };
  tableColumnList: string[] = [
    'select',
    'name',
    'email',
    'phone',
    'status',
    'emailCount',
    'lastopened',
    'created',
    'address',
    'substatus',
  ];
  tableDataSourceInfo: MatTableDataSource<Campaign>;
  selection = new SelectionModel<any>(true, []);
  pageSize: any;
  pageIndex: number;
  searchValue: string = '';
  model: string;
  modelChanged: Subject<string> = new Subject<string>();
  typingTimeout: any;
  isLogicExecuted: boolean = false;
  selectedRecordIdList = [];
  totalRun: number = 0;
  isLoadingCampaignStatistics: boolean = false;
  frequency: string;
  constant: any = constants;

  constructor(
    private campaignService: CampaignService,
    public campaignState: CampaignState,
    private contactState: ContactState,
    public appState: AppState,
    public _pktrAppMenuService: PktrAppMenuService,
    public breakpointService: ValidatorsService
  ) { }
  sortFields: Array<any> = [
    {
      displayValue: 'Read',
      paramValue: 'read',
    },
    {
      displayValue: 'Unread',
      paramValue: 'unread',
    },
  ];
  defaultSort: any = this.sortFields[1];
  onDestroyNotifier$ = new Subject();
  totalRecords: number;
  ngOnInit(): void {
    this.getCampaignInfo();
    this.modelChanged.pipe(debounceTime(600)).subscribe((searchValue) => {
      this.handleSearch(searchValue);
    });
    this.campaignState.campaignInfo$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      this.tableDataSourceInfo = new MatTableDataSource(res?.data?.records || []);
      this.totalRecords = res?.data?.totalRecordCount;
      if (res?.data?.records) {
        this.frequency = null;
        const sub = res.data.records[0].userSubscription.subscriptions;
        this.frequency = sub.frequency;
        const f = this.frequency?.toLowerCase();
        const start = new Date(this.selectedInfo?.launchDate);
        const now = new Date();
        const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
        let totalRun = 0;

        if (start <= now) {
          switch (f) {
            case 'one_time':
              this.frequency = null;
              totalRun = 0;
              break;

            case 'monthly':
              totalRun = months + 1;
              break;

            case 'quarterly':
              totalRun = Math.floor(months / 3) + 1;
              break;

            case 'yearly':
              totalRun = now.getFullYear() - start.getFullYear() + 1;
              break;

            case 'every_n_months':
              this.frequency = sub.everyNMonths + ' Months';
              const n = sub.everyNMonths || 1; // 👈 dynamic value like 3
              totalRun = Math.floor(months / n) + 1;
              break;

            default:
              totalRun = 0;
          }
        }

        this.totalRun = totalRun;
      }
    });
  }
  changed(text: string) {
    this.modelChanged.next(text);
  }

  getCampaignTypeKey(value: string): string {
    const types = this.constant.CAMPAIGN_TYPE;
    return (
      Object.keys(types)
        .find((key) => types[key] === value)
        ?.replace(/([A-Z])/g, ' $1')
        ?.replace(/^./, (str) => str.toUpperCase()) || ''
    );
  }

  getCampaignInfo() {
    this.isLoadingCampaignStatistics = true;
    this.campaignState.campaignInfoValue = null;
    let params = {
      campaignId: this.selectedInfo._id,
      searchQuery: this.infoFetchParams.searchQuery,
      limit: this.infoFetchParams.limit,
      offset: this.infoFetchParams.offset,
      emailStatus: this.selectedEmailStatus == 'all' ? null : this.infoFetchParams.emailStatus,
    };
    this.campaignService
      .getCampaignInfo(params)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res: any) => {
        if (res && res.response) {
          this.isLoadingCampaignStatistics = false;
        }
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier$?.next();
    this.onDestroyNotifier$?.complete();
    this.contactState.selectedContactsListValue = null;
  }

  onSortChange(event: MatSelectChange) { }
  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selectedRecordIdList.length;
    const numRows = this.tableDataSourceInfo.data.filter((record) => !record['unSubscribeAll']).length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle(event) {
    this.isAllSelected()
      ? this.selection.clear()
      : this.tableDataSourceInfo.data.forEach((row) => {
        if (!row['unSubscribeAll']) {
          this.selection.select(row);
        }
      });
    if (event.checked) {
      this.selectedRecordIdList = this.tableDataSourceInfo.data.filter((record) => !record['unSubscribeAll']);
    } else {
      this.selectedRecordIdList = [];
    }
  }

  toggleSelect(row, checked) {
    const selectedIdSet = new Set(this.selectedRecordIdList);
    if (checked) {
      selectedIdSet.add(row);
    } else {
      for (const item of selectedIdSet.values()) {
        if (item._id == row._id) selectedIdSet.delete(item);
      }
    }
    this.selectedRecordIdList = [...selectedIdSet];
  }

  openStatusSheet(selected) {
    if (this.appState?.appCompanyAliasValue == 'valuetest' || this.selectedInfo.type != 'CAMP_IR_INVT') {
      this.statusId = selected;
      this.campaignState.openCampaignStatusSheetValue = true;
    }
  }
  openCampaignSheet() {
    this.campaignState.openCreateCampaignSheetValue = true;
    this.contactState.selectedContactsListValue = {
      selectedRecords: this.selectedRecordIdList,
      selectedInfo: this.selectedInfo,
      selectedAll: this.isAllSelected(),
    };
  }

  openEditCampaignSheet() {
    this.breakpointService
      .isMobileOrTablet()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((isMobileOrTablet) => {
        if (isMobileOrTablet) {
          this._pktrAppMenuService.close(true);
        }

      });
    let campaignInfo = this.selectedInfo;
    campaignInfo.mode = 'addContact';
    this.campaignState.openCreateCampaignSheetValue = true;
    this.campaignState.campaignToEditValue = campaignInfo;
  }

  pageChange(event) {
    if (this.infoFetchParams.offset != event.pageSize * event.pageIndex) {
      this.infoFetchParams.limit = event.pageSize;
      this.infoFetchParams.offset = event.pageSize * event.pageIndex;
    } else {
      this.infoFetchParams.limit = event.pageSize;
      this.paginator.pageIndex = 0;
      this.infoFetchParams.offset = 0;
    }
    this.getCampaignInfo();
  }

  handleSearch(value) {
    this.infoFetchParams.searchQuery = value;
    this.infoFetchParams.limit = 10;
    this.infoFetchParams.offset = 0;
    this.getCampaignInfo();
  }

  emailStatus(event) {
    this.infoFetchParams.emailStatus = event.value;
    this.infoFetchParams.limit = 10;
    this.infoFetchParams.offset = 0;
    this.getCampaignInfo();
  }
  closeEvent() {
    this.isOpenInfoEvent.emit(false);
  }
}
